<template>
  <BExpandableCard
    :default-expanded="defaultExpanded"
    :expandable="expandable"
  >
    <template #info>
      <div class="lead-card-info">
        <div class="lead-card-info-title">
          <LeadLink
            :call-target-id="callTarget.id"
          >
            {{ callTarget.callTargetInfo.name || `ID: ${callTarget.id}` }}
          </LeadLink>
        </div>
        <BLeadStagePreview
          :lead-stage="callTarget.leadStage"
        />
      </div>
    </template>
    <div
      v-for="attributeKey in attributeKeys"
      :key="attributeKey"
      class="lead-card-attribute"
    >
      <template v-if="shouldUseSpecificComponent(attributeKey)">
        <template v-if="isCompanyInfo(attributeKey) || isCallTargetInfo(attributeKey)">
          <LeadDetailItem
            :info="getAttributeInfoByKey(attributeKey)"
          />
        </template>
        <template v-else-if="isCustomField(attributeKey)">
          <CustomFieldItem
            :item="getCustomFieldByKey(attributeKey)"
            :call-target-id="callTarget.id"
            :sf-reference-info="sfReferenceInfo"
          />
        </template>
      </template>
      <BListItem v-else>
        <template #header>
          <span>{{ $t(`callTarget.${attributeKey}`) }}</span>
        </template>
        <template v-if="isUser(attributeKey)">
          <UserPreview
            :user="callTarget.user"
          />
        </template>
      </BListItem>
    </div>
  </BExpandableCard>
</template>

<script lang="ts" setup>
import type { CallTarget } from '@/api/openapi';
import LeadDetailItem from '@/components/organisms/user/account/LeadDetailItem.vue';
import { useLeadAttributes, useSfReferenceInfo } from '@/composable/user/lead/attributes';
import LeadLink from '../general/LeadLink.vue';
import CustomFieldItem from './CustomFieldItem.vue';

type TProps = {
  callTarget: CallTarget;
  // TODO: ['companyInfo.companyName', 'freeTag', 'user', 'customField.123'] などが入ってくる想定（暫定なので表示項目設定の実装時に修正してください
  // キーの構成はTargetDetailと同様に実装しておくと、実装が早いはず
  // NOTE: 会社情報・リードソース・タグ・SFキャンペーン は表示しないという前提で実装しています
  attributeKeys: string[];
  defaultExpanded?: boolean;
  expandable?: boolean;
};

const props = withDefaults(defineProps<TProps>(), {
  defaultExpanded: false,
  expandable: true,
});

const { getAttributeInfoByKey } = useLeadAttributes(() => props.callTarget);
const { sfReferenceInfo } = useSfReferenceInfo(() => props.callTarget.id);

// TODO: 各種キー・ラベルは上記想定に則って実装している。適宜修正してください
const getCustomFieldByKey = (key: string) => {
  const [, id] = key.split('.');

  return props.callTarget.customFields.find((customField) => customField.id === Number(id));
};

const isUser = (key: string) => {
  return key === 'user';
};
const isCompanyInfo = (key: string) => {
  return key.startsWith('companyInfo');
};
const isCallTargetInfo = (key: string) => {
  return key.startsWith('callTargetInfo');
};
const isCustomField = (key: string) => {
  return key.startsWith('customField');
};
const shouldUseSpecificComponent = (key: string) => {
  return isCompanyInfo(key) || isCallTargetInfo(key) || isCustomField(key);
};
</script>

<style lang="scss" scoped>
.lead-card-info-title {
  margin-bottom: 10px;
  font-weight: $font-weight-bold;
}
.lead-stage-preview {
  font-size: $fontsize-100;
}
.lead-card-attribute {
  margin-bottom: $basespace-400;
  &:last-child {
    margin-bottom: 0;
  }
  .b-list-item {
    margin-bottom: 0;
  }
}
</style>
